// FormCab.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../../../api/apiClient';
import * as bootstrap from 'bootstrap';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { generateDescription, generateOverview } from "../../custom/geminiService";
import CustomEditor from '../../custom/CustomEditor';

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['places'];

const isEditorEmpty = (htmlString) => {
  if (!htmlString) return true;
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  return tempDiv.textContent.trim().length === 0;
};

const InputField = ({ name, placeholder, value, onChange, error, type = "text", ...props }) => (
  <div
    className={`floating-field position-relative ${value ? 'has-value' : ''}`}
    data-bs-toggle={error ? "tooltip" : undefined}
    data-bs-placement="top"
    title={error || ""}
  >
    <input
      type={type}
      name={name}
      className={`form-control ${error ? 'is-invalid' : ''}`}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      {...props}
    />
    <label className="floating-label">{placeholder}</label>
  </div>
);

function FormCab() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries,
  });

  // Autocomplete refs for address, source, destination
  const addressAutoRef = useRef(null);
  const sourceAutoRefs = useRef([]);
  const destAutoRef = useRef(null);

  const collapseTwoRef = useRef(null);
  const collapseThreeRef = useRef(null);

  const [formData, setFormData] = useState({
    user_id: '', // optional - admin can assign owner
    brand_name: '',
    cab_city: '',
    year_of_establishment: '',
    fuel_type: '',
    accommodation_type: '', // 'AC' or 'Non-AC'
    seating_capacity: '',
    cab_contact_person: '',
    cab_contact_number: '',
    cab_address: '',
    source_from: '',
    source_points: [''],
    destination_to: '',
    km_included: '',
    post_limit: '',
    package_price: '',
    offer_price: '',
    cab_description: '',
    cab_overview: '',
  });

  const [existingImages, setExistingImages] = useState([]); // from backend, array of { image_url, image_size_name, ... }
  const [newImages, setNewImages] = useState([]); // [{file, url}]
  const [imagesToRemove, setImagesToRemove] = useState([]); // array of image_url strings or ids, whatever backend expects
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingAIOverview, setIsGeneratingAIOverview] = useState(false);
  const [isGeneratingAIDescription, setIsGeneratingAIDescription] = useState(false);

  // group existing images by base name (same approach as your hotel form)
  const imageGroups = useMemo(() => {
    if (!existingImages || existingImages.length === 0) return [];
    const groups = existingImages.reduce((acc, image) => {
      const urlParts = image.image_url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const lastUnderscore = fileName.lastIndexOf('_');
      const baseName = lastUnderscore > 0 ? fileName.substring(0, lastUnderscore) : fileName;
      if (!acc[baseName]) acc[baseName] = [];
      acc[baseName].push(image);
      return acc;
    }, {});
    return Object.values(groups);
  }, [existingImages]);

  useEffect(() => {
    const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    const tooltipList = tooltipTriggerList.map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl));
    return () => tooltipList.forEach(t => t && t.dispose());
  }, [errors]);

  // load cab (edit mode)
  useEffect(() => {
    if (!isEditMode) return;
    const fetchCab = async () => {
      try {
        const res = await apiClient.get(`/cabs/${id}`);
        const cab = res.data;
        // If backend returns images in 'Images' key similar to hotels:
        setExistingImages(cab.Images || []);
        const { Images, ...rest } = cab;
        const source_points = (() => {
          if (Array.isArray(cab.source_points)) return cab.source_points;
          if (typeof cab.source_points === 'string') {
            try {
              const parsed = JSON.parse(cab.source_points);
              return Array.isArray(parsed) ? parsed : [cab.source_points];
            } catch {
              return cab.source_points ? [cab.source_points] : [''];
            }
          }
          if (cab.source_from) return [cab.source_from];
          return [''];
        })();
        setFormData(prev => ({ ...prev, ...rest, source_points }));
      } catch (err) {
        toast.error("Failed to load cab data. Redirecting...");
        navigate('/admin/cabs');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCab();
  }, [id, isEditMode, navigate]);

  useEffect(() => {
    if (loadError) {
      toast.error("Google Maps script could not be loaded. Autocomplete will not work.");
      console.error("Google Maps load error:", loadError);
    }
  }, [loadError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleEditorChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) setErrors(prev => ({ ...prev, [fieldName]: null }));
  };


  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if ((imageGroups.length + newImages.length + files.length) > 6) {
      toast.warn("You can upload a maximum of 6 images in total.");
      return;
    }
    const filesWithPreview = files.map(f => ({ file: f, url: URL.createObjectURL(f) }));
    setNewImages(prev => [...prev, ...filesWithPreview]);
    if (errors.images) setErrors(prev => ({ ...prev, images: null }));
  };

  const handleRemoveNewImage = (index) => {
    const copy = [...newImages];
    URL.revokeObjectURL(copy[index].url);
    copy.splice(index, 1);
    setNewImages(copy);
  };

  const handleRemoveExistingImage = (imageGroup) => {
    const repUrl = imageGroup[0]?.image_url;
    if (!repUrl) return;
    setImagesToRemove(prev => [...prev, repUrl]);
    // remove all images with that base name
    const baseName = repUrl.substring(repUrl.lastIndexOf('/') + 1, repUrl.lastIndexOf('_'));
    setExistingImages(prev => prev.filter(img => !img.image_url.includes(baseName)));
  };

  const handleSourcePointChange = (index, value) => {
    setFormData(prev => {
      const source_points = Array.isArray(prev.source_points) ? [...prev.source_points] : [''];
      source_points[index] = value;
      return { ...prev, source_points };
    });
  };

  const addSourcePoint = () => {
    setFormData(prev => {
      const currentSources = Array.isArray(prev.source_points) ? prev.source_points : [''];
      if (currentSources.length >= 10) {
        toast.error('Your limit is over. A maximum of 10 source points is allowed.');
        return prev;
      }
      return {
        ...prev,
        source_points: [...currentSources, '']
      };
    });
  };

  const removeSourcePoint = (index) => {
    setFormData(prev => {
      const source_points = Array.isArray(prev.source_points) ? [...prev.source_points] : [];
      source_points.splice(index, 1);
      return { ...prev, source_points: source_points.length ? source_points : [''] };
    });
  };

  // Autocomplete handlers (address / source / destination)
  // const handlePlaceSelect = (autocompleteRef, targetField) => {
  //   const ac = autocompleteRef.current;
  //   if (!ac) return;
  //   const place = ac.getPlace();
  //   if (!place || !place.address_components) {
  //     toast.warn("Please select a valid place from suggestions.");
  //     return;
  //   }
  //   const componentMap = {
  //     locality: 'cab_city',
  //     administrative_area_level_1: 'state',
  //     country: 'country',
  //     postal_code: 'pincode',
  //   };
  //   // We'll set the chosen formatted address on the target field (cab_address / source_from / destination_to)
  //   setFormData(prev => ({ ...prev, [targetField]: place.formatted_address || place.name || prev[targetField] }));
  //   // clear possible error
  //   setErrors(prev => ({ ...prev, [targetField]: null }));
  // };
  const placeToLabel = (place) => {
    if (!place) return '';
    if (place.formatted_address) return place.formatted_address;
    if (place.name) return place.name;
    if (place.address_components && place.address_components.length) {
      const comp = place.address_components;
      const findType = (type) => {
        const c = comp.find(x => x.types && x.types.includes(type));
        return c ? c.long_name : null;
      };
      const parts = [findType('locality'), findType('administrative_area_level_1'), findType('country')].filter(Boolean);
      return parts.join(', ') || '';
    }
    return '';
  };

  const handlePlaceSelect = (autocompleteInstance, targetField, index = null) => {
    const ac = autocompleteInstance?.current || autocompleteInstance;
    if (!ac) return;
    const place = ac.getPlace();
    const label = placeToLabel(place);
    if (!label) {
      toast.warn("Please select a valid place from suggestions.");
      return;
    }
    setFormData(prev => {
      if (targetField === 'source_points' && typeof index === 'number') {
        const source_points = Array.isArray(prev.source_points) ? [...prev.source_points] : [''];
        source_points[index] = label;
        return { ...prev, source_points };
      }
      return { ...prev, [targetField]: label };
    });
    setErrors(prev => ({ ...prev, [targetField]: null }));
  };

  /* const handleGenerateOverview = async () => {
     const { brand_name, cab_city } = formData;
     if (!brand_name || !cab_city) {
       toast.warn("Provide Brand name and City before generating overview.");
       return;
     }
     setIsGeneratingAIOverview(true);
     try {
       const ai = await generateOverviewCabs(brand_name, cab_city, formData.package_price || '');
       handleEditorChange('cab_overview', ai);
       toast.success("AI overview generated.");
     } catch (err) {
       toast.error("Failed to generate overview.");
       console.error(err);
     } finally {
       setIsGeneratingAIOverview(false);
     }
   };*/

  const handleGenerateDescription = async () => {
    const { brand_name, cab_city } = formData;
    if (!brand_name || !cab_city) {
      toast.warn("Provide Brand name and City before generating description.");
      return;
    }
    setIsGeneratingAIDescription(true);
    try {
      const ai = await generateDescription(brand_name, cab_city);
      handleEditorChange('cab_description', ai);
      toast.success("AI description generated.");
    } catch (err) {
      toast.error("Failed to generate description.");
      console.error(err);
    } finally {
      setIsGeneratingAIDescription(false);
    }
  };

  const validateForm = () => {
    const newErr = {};
    if (!String(formData.brand_name || '').trim()) newErr.brand_name = 'Brand name required.';
    if (!String(formData.cab_city || '').trim()) newErr.cab_city = 'City required.';
    if (!String(formData.year_of_establishment || '').trim()) newErr.year_of_establishment = 'Year of establishment required.';
    if (!String(formData.cab_address || '').trim()) newErr.cab_address = 'Address required.';
    if (!formData.fuel_type) newErr.fuel_type = 'Select Fuel Type';
    if (!formData.accommodation_type) newErr.accommodation_type = 'Select AC / Non-AC.';
    // if (!String(formData.year_of_establishment || '').trim()) newErr.year_of_establishment = 'Year of establishment required.';

    if (!String(formData.seating_capacity || '').trim()) newErr.seating_capacity = 'Seating capacity required.';
    if (!String(formData.km_included || '').trim()) newErr.km_included = 'Km included required.';
    if (!String(formData.post_limit || '').trim()) newErr.post_limit = 'Post limit required.';
    if (!formData.cab_contact_person) newErr.cab_contact_person = 'Contact person required.';
    if (!formData.cab_contact_number) newErr.cab_contact_number = 'Contact number required.';
    if (!formData.package_price) newErr.package_price = 'Package price required.';
    else if (isNaN(formData.package_price) || +formData.package_price <= 0) newErr.package_price = 'Price must be > 0.';
    if (formData.offer_price && (isNaN(formData.offer_price) || +formData.offer_price < 0)) newErr.offer_price = 'Offer price invalid.';
    // if (!String(formData.cab_description || '').trim()) newErr.cab_description = 'Short description required.';
    if (isEditorEmpty(formData.cab_description)) newErr.cab_description = 'Description required.';
    if (imageGroups.length === 0 && newImages.length === 0) newErr.images = 'Please upload at least one image.';
    if (!Array.isArray(formData.source_points) || !formData.source_points.some(Boolean)) newErr.source_points = 'Add at least one source.';
    return newErr;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      const errorKeys = Object.keys(formErrors);
      if (errorKeys.includes('images')) {
        const collapseEl = collapseTwoRef.current;
        if (collapseEl) bootstrap.Collapse.getOrCreateInstance(collapseEl).show();
      }
      const thirdErrors = ['cab_overview'];
      if (errorKeys.some(k => thirdErrors.includes(k))) {
        const collapseEl = collapseThreeRef.current;
        if (collapseEl) bootstrap.Collapse.getOrCreateInstance(collapseEl).show();
      }
      setErrors(formErrors);
      toast.error("Please fix errors.");
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const uploadData = new FormData();
    // Append all fields except readonly/meta
    Object.entries(formData).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (k === 'source_points') {
        uploadData.append(k, JSON.stringify((Array.isArray(v) ? v : [v]).filter(Boolean)));
        return;
      }
      uploadData.append(k, v);
    });

    // include image type explicitly (backend should use this or infer)
    uploadData.append('image_type', 'cab');

    // append new images
    newImages.forEach(img => uploadData.append('images', img.file));

    // when editing, add imagesToRemove if any and include id
    if (imagesToRemove.length > 0) {
      uploadData.append('imagesToRemove', JSON.stringify(imagesToRemove));
    }

    try {
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      let successMessage = '';
      if (isEditMode) {
        await apiClient.put(`/cabs/${id}`, uploadData, config);
        successMessage = 'Cab updated successfully!';
      } else {
        await apiClient.post('/cabs', uploadData, config);
        successMessage = 'Cab created successfully!';
      }
      toast.success(successMessage);
      navigate('/admin/cabs', { state: { message: successMessage } });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Submission failed. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && isEditMode) {
    return <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>;
  }

  return (
    <div>
      <div className="row mb-3">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">{isEditMode ? 'Edit Cab' : 'Create Cab'}</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/admin/cabs">Cabs</Link></li>
                <li className="breadcrumb-item active">{isEditMode ? 'Edit' : 'Add'}</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="card">
          <div className="card-header"><h5 className="card-title mb-0">{isEditMode ? 'Edit Cab Details' : 'Add New Cab'}</h5></div>
          <div className="card-body">
            <div className="accordion" id="cabAccordion">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingOne">
                  <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne">Cab Details</button>
                </h2>
                <div id="collapseOne" className="accordion-collapse collapse show" data-bs-parent="#cabAccordion">
                  <div className="accordion-body">
                    <div className="row g-3">

                      <div className="col-md-3"><InputField name="brand_name" placeholder="Brand (e.g., Toyota)" value={formData.brand_name} onChange={handleInputChange} error={errors.brand_name} readOnly={isEditMode} /></div>
                      <div className="col-md-3"><InputField name="cab_city" placeholder="City" value={formData.cab_city} onChange={handleInputChange} error={errors.cab_city} /></div>
                      <div className="col-md-3"><InputField name="year_of_establishment" placeholder="Year of establishment" value={formData.year_of_establishment} onChange={handleInputChange} error={errors.year_of_establishment} /></div>

                      <div className="col-md-3">
                        <div className={`floating-field position-relative ${formData.fuel_type ? 'has-value' : ''}`} data-bs-toggle={errors.fuel_type ? "tooltip" : undefined} data-bs-placement="top" title={errors.fuel_type || ""}>
                          {/* <input name="fuel_type" placeholder="Fuel type (Petrol/Diesel/Electric)" className={`form-control ${errors.fuel_type ? 'is-invalid' : ''}`} value={formData.fuel_type} onChange={handleInputChange} /> */}
                          <select name="fuel_type" className={`form-select ${errors.fuel_type ? 'is-invalid' : ''}`} value={formData.fuel_type} onChange={handleInputChange}>
                            <option value="">Select Fuel Type</option>
                            <option value="Petrol">Petrol</option>
                            <option value="Diesel">Diesel</option>
                            <option value="Electric">Electric</option>
                          </select>
                          <label className="floating-label">Select Fuel Type</label>
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className={`floating-field position-relative ${formData.accommodation_type ? 'has-value' : ''}`} data-bs-toggle={errors.accommodation_type ? "tooltip" : undefined} data-bs-placement="top" title={errors.accommodation_type || ""}>
                          <select name="accommodation_type" className={`form-select ${errors.accommodation_type ? 'is-invalid' : ''}`} value={formData.accommodation_type} onChange={handleInputChange}>
                            <option value="">Select Accomodation</option>
                            <option value="AC">AC</option>
                            <option value="Non-AC">Non-AC</option>
                          </select>
                          <label className="floating-label">Select Accomodation</label>
                        </div>
                      </div>

                      <div className="col-md-3"><InputField name="seating_capacity" placeholder="Seating capacity" value={formData.seating_capacity} onChange={handleInputChange} error={errors.seating_capacity} /></div>
                      <div className="col-md-3"><InputField name="km_included" placeholder="KM included" value={formData.km_included} onChange={handleInputChange} error={errors.km_included} /></div>
                      <div className="col-md-3"><InputField name="post_limit" placeholder="Post limit (e.g., per day km)" value={formData.post_limit} onChange={handleInputChange} error={errors.post_limit} /></div>

                      <div className="col-md-3"><InputField name="cab_contact_person" placeholder="Contact person" value={formData.cab_contact_person} onChange={handleInputChange} error={errors.cab_contact_person} /></div>
                      <div className="col-md-3"><InputField name="cab_contact_number" placeholder="Contact number" value={formData.cab_contact_number} onChange={handleInputChange} error={errors.cab_contact_number} /></div>

                      <div className="col-md-3"><InputField name="package_price" type="number" placeholder="Package Price (₹)" value={formData.package_price} onChange={handleInputChange} error={errors.package_price} /></div>
                      <div className="col-md-3"><InputField name="offer_price" type="number" placeholder="Offer Price (₹)" value={formData.offer_price} onChange={handleInputChange} error={errors.offer_price} /></div>

                      <div className="col-12">
                        <div className={`floating-field position-relative ${formData.cab_address ? 'has-value' : ''}`} data-bs-toggle={errors.cab_address ? "tooltip" : undefined} data-bs-placement="top" title={errors.cab_address || ""}>
                          <textarea name="cab_address" className={`form-control ${errors.cab_address ? 'is-invalid' : ''}`} placeholder="Enter short address..." value={formData.cab_address} onChange={handleInputChange} rows={3} />
                          <label className="floating-label">Enter short address...</label>
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label mb-0 fw-medium">Cab Overview (Inclusions and Exclusions)</label>
                        </div>
                        <div className={errors.cab_description ? 'is-invalid-editor' : ''} data-bs-toggle={errors.cab_description ? "tooltip" : undefined} data-bs-placement="top" title={errors.cab_description || ""}>
                          <CustomEditor placeholder="A long summery with inclusions and exclusions..." data={formData.cab_description} onChange={(val) => handleEditorChange('cab_description', val)} showHtmlMode={false} />
                        </div>
                        {errors.cab_description && <div className="d-block mt-1 text-danger" style={{ fontSize: '.875em' }}>{errors.cab_description}</div>}
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              {/* Images */}
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingTwo">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo">Upload Cab Images</button>
                </h2>
                <div id="collapseTwo" ref={collapseTwoRef} className="accordion-collapse collapse" data-bs-parent="#cabAccordion">
                  <div className="accordion-body">
                    <div className="alert alert-info"><strong>Note:</strong> Up to 6 images. Image type will be stored as <code>cab</code>.</div>
                    <div className="d-flex flex-wrap gap-3">
                      {imageGroups.map((group, gIdx) => {
                        const preview = group.find(img => img.image_size_name === 'thumbnail') || group[0];
                        if (!preview) return null;
                        return (
                          <div key={`existing-${gIdx}`} className="position-relative" style={{ width: 120, height: 120 }}>
                            <img src={preview.image_url} alt={`img-${gIdx}`} className="img-thumbnail h-100 w-100" style={{ objectFit: 'cover' }} />
                            <button type="button" onClick={() => handleRemoveExistingImage(group)} className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-1">×</button>
                          </div>
                        );
                      })}

                      {newImages.map((img, idx) => (
                        <div key={`new-${idx}`} className="position-relative" style={{ width: 120, height: 120 }}>
                          <img src={img.url} alt={`new-${idx}`} className="img-thumbnail h-100 w-100" style={{ objectFit: 'cover' }} />
                          <button type="button" onClick={() => handleRemoveNewImage(idx)} className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-1">×</button>
                        </div>
                      ))}

                      {(imageGroups.length + newImages.length) < 6 && (
                        <label htmlFor="uploadImages" className="d-flex justify-content-center align-items-center" style={{ width: 120, height: 120, border: `2px dashed ${errors.images ? '#dc3545' : '#aaa'}`, borderRadius: 6, cursor: 'pointer', fontSize: 32, color: '#aaa' }}>
                          +
                          <input id="uploadImages" type="file" multiple accept="image/*" hidden onChange={handleImageChange} />
                        </label>
                      )}
                    </div>
                    {errors.images && <div className="d-block mt-2 text-danger" style={{ fontSize: '.875em' }}>{errors.images}</div>}
                  </div>
                </div>
              </div>

              {/* Source / Destination */}
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingThree">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree">Source points</button>
                </h2>
                <div id="collapseThree" ref={collapseThreeRef} className="accordion-collapse collapse" data-bs-parent="#cabAccordion">
                  <div className="accordion-body">
                    <div className="row g-3">

                      <div className="col-12">
                        <label className="form-label">Source points</label>
                        {Array.isArray(formData.source_points) && formData.source_points.map((source, index) => (
                          <div key={`source-point-${index}`} className="mb-3">
                            {isLoaded ? (
                              <Autocomplete
                                onLoad={inst => {
                                  sourceAutoRefs.current[index] = inst;
                                  try { inst.setFields(['formatted_address', 'name', 'address_components', 'geometry', 'place_id']); } catch (e) { /* ignore if not supported */ }
                                }}
                                onPlaceChanged={() => handlePlaceSelect(sourceAutoRefs.current[index], 'source_points', index)}
                                options={{ componentRestrictions: { country: 'in' } }}
                              >
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className={`form-control ${index === 0 && errors.source_points ? 'is-invalid' : ''}`}
                                    placeholder={`Source ${index + 1}`}
                                    value={source}
                                    onChange={(e) => handleSourcePointChange(index, e.target.value)}
                                  />
                                  <button type="button" className="btn btn-outline-danger" onClick={() => removeSourcePoint(index)} disabled={formData.source_points.length === 1}>Remove</button>
                                </div>
                              </Autocomplete>
                            ) : (
                              <div className="input-group">
                                <input
                                  type="text"
                                  className={`form-control ${index === 0 && errors.source_points ? 'is-invalid' : ''}`}
                                  placeholder={`Source ${index + 1}`}
                                  value={source}
                                  onChange={(e) => handleSourcePointChange(index, e.target.value)}
                                />
                                <button type="button" className="btn btn-outline-danger" onClick={() => removeSourcePoint(index)} disabled={formData.source_points.length === 1}>Remove</button>
                              </div>
                            )}
                          </div>
                        ))}
                        <button type="button" className="btn btn-sm btn-primary" onClick={addSourcePoint}>Add source</button>
                        {formData.source_points.length >= 10 && (
                          <div className="form-text text-warning">Maximum 10 source points allowed.</div>
                        )}
                        {errors.source_points && <div className="form-text text-danger">{errors.source_points}</div>}
                      </div>

                    </div>
                  </div>
                </div>
              </div>

            </div>

            <div className="mt-4 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light" onClick={() => navigate('/admin/cabs')} disabled={isSubmitting}>Cancel</button>
              <button type="submit" className="btn btn-success" disabled={isSubmitting || isGeneratingAIDescription || isGeneratingAIOverview}>{isSubmitting ? 'Submitting...' : (isEditMode ? 'Update Cab' : 'Create Cab')}</button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default FormCab;
